import { Component, ElementRef, EventEmitter, Input, Output, ViewChild
       } from '@angular/core';

import { FontInjectorService } from '../collage-designer/font-injector.service';
import { TextPlaceholder
       } from '../collage-designer/stage/placeholder/text-placeholder';
import { Utils } from '../core/utils';
import { FontDescription, insertChunks, plaintextToChunks, selectCompleteText,
         Text, TextChunk, TextChunkKind, textsAreEqual,
       } from './text.model';
import { applyModel, extractTextChunks } from './text.view.div';

enum TextViewState {
  Initial,
  Reopened,
  Blurred,
  ReceivesFocus,
  Focused
}

@Component({
  selector: 'editable-text-view',
  templateUrl: './text.view.div.component.html',
  styleUrls: [ './text.view.div.component.scss' ]
})
export class EditableTextViewComponent
{
  private static readonly FOCUS_GRACE_TIME: number = 300; /* ms */
  @ViewChild('textArea') private textArea: ElementRef;

  private model: Text | undefined = undefined;
  private font: FontDescription | undefined = undefined;
  private textViewState: TextViewState = TextViewState.Initial;

  @Output('textChange') public textChange: EventEmitter<Array<TextChunk>> =
    new EventEmitter();

  constructor(private fontInjector: FontInjectorService)
  {}

  @Input('placeholder') public set placeholder(placeholder: TextPlaceholder)
  {
    if (!placeholder.isUnchanged()) {
      this.textViewState = TextViewState.Reopened;
    }
  }

  @Input() public set textModel(model: Text | undefined)
  {
    this.model = model;
    this.updateView();
  }

  public onInput(ev: Event): void
  {
    if (! (<any>ev).isComposing) {
      this.emitTextChange();
    }
  }

  public onBlur(): void
  {
    this.textViewState = TextViewState.Blurred;
    this.emitTextChange();
  }

  public onFocus(): void
  {
    // This is a weird and ugly workaround. When the user focuses the text field
    // on a mobile device, the keyboard will pop up and trigger a resize event.
    // This event will insert a model with a different cursor position,
    // resulting in a jumping cursor. To mitigate this we emit the cursor
    // position at the time the user focuses the text field. But because the
    // selection is set after the focus event we have to wait a little.

    if (! Utils.isSmallDevice()) {
      return;
    }

    const initial = this.textViewState === TextViewState.Initial;
    this.textViewState = TextViewState.ReceivesFocus;

    setTimeout(() => {
      this.textViewState = TextViewState.Focused;
      this.textChange.emit(
        initial ? selectCompleteText(this.model).text : this.textChunks);
    }, EditableTextViewComponent.FOCUS_GRACE_TIME);
  }

  private withoutControlChunks(text: Text): Text
  {
    return {
      ...text,
      text: text.text.filter(c => c.kind !== TextChunkKind.Control)
    };
  }

  private async updateView(): Promise<void>
  {
    if (! this.model) {
      return;
    }

    if (!this.font || this.font.name !== this.model.fontName) {
      this.font = await this.fontInjector.getFontDescription(this.model);
    }

    const displayModel = (() => {
      if (! Utils.isSmallDevice()) {
        return this.model;
      }

      switch (this.textViewState) {
        case TextViewState.ReceivesFocus:
          return undefined;
        case TextViewState.Blurred:
          return this.withoutControlChunks(this.model);
        case TextViewState.Reopened:
        case TextViewState.Focused:
          return this.model;
        case TextViewState.Initial:
          return selectCompleteText(this.model);
        default:
          return undefined;
      }
    })();

    if (! displayModel) {
      return;
    }

    applyModel(this.textArea.nativeElement, displayModel, this.font);

    if (!Utils.isSmallDevice() ||
        this.textViewState === TextViewState.Reopened) {
      this.textArea.nativeElement.focus();
    }
  }

  private get textChunks(): Array<TextChunk>
  {
    return extractTextChunks(this.textArea.nativeElement);
  }

  private emitTextChange(): void
  {
    const newText = this.textChunks;

    if (textsAreEqual(this.model.text, newText)) {
      return;
    }

    this.textChange.emit(newText);
  }

  public onPaste(pasteEvent: any): void
  {
    pasteEvent.stopPropagation();
    pasteEvent.preventDefault();

    const text = (pasteEvent.clipboardData || (<any>window).clipboardData)
      .getData('text');

    this.textChange.emit(
      insertChunks(this.textChunks, plaintextToChunks(text)));
  }
}



// WEBPACK FOOTER //
// ./src/app/text/text.view.div.component.ts